import {
	API_ENDPOINTS,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	customToast,
	DateFormats,
	Dropdown,
	formatDateFns,
	Icons,
	Modal,
	PageLayout,
	Permissions,
	replaceKeyWithValue,
	route,
	StringHelpers,
	SubTitle,
	useAuthorisationContext,
	useDisabledContext,
	useForm,
	useLangContext,
	useStore,
	Variants,
	WidthConstrainedContainer,
} from 'carrier-fe';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AdminClaimViewData, ClaimPart } from '../../../types/claims';
import { DropdownItemProps } from 'carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem';
import { DISTRIBUTOR_CLAIM_TYPES, SEARCH_FILTERS } from '../../../Constants';
import ClaimEventLog from '../eventLog';
import exportClaims from '../helpers/export-claims';

type StatusModalType = 'reviewed' | 'ordered' | 'shipped' | null;
type ActionModalType = 'approve' | 'decline' | 'claim_value' | null;

function ClaimView() {
	const { claimId } = useParams();
	const navigate = useNavigate();
	const { crud, fields, responses } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();
	const { hasPermissionAccess } = useAuthorisationContext();

	const { store, setValues } = useStore<AdminClaimViewData>(
		{} as AdminClaimViewData
	);
	const [activeStatusModal, setActiveStatusModal] = useState<StatusModalType>(null);
	const [activeActionModal, setActiveActionModal] = useState<ActionModalType>(null);
	const [eventLogRefetchKey, setEventLogRefetchKey] = useState(0);

	const triggerEventLogRefetch = () => setEventLogRefetchKey(prev => prev + 1);

	const openStatusModal = (modalType: StatusModalType) => setActiveStatusModal(modalType);
	const closeStatusModal = () => setActiveStatusModal(null);

	const openActionModal = (modalType: ActionModalType) => setActiveActionModal(modalType);
	const closeActionModal = () => {
		setActiveActionModal(null);
		declineForm.setErrors({});
		claimValueForm.setErrors({});
	};

	// Decline form
	const defaultDeclineData = {
		declined_reason: '',
	};

	const submitDecline = async (data: typeof defaultDeclineData) => {
		return axios.patch(
			route(API_ENDPOINTS.ADMIN.CLAIM.DECLINE, {
				claimId: String(claimId),
			}),
			data
		);
	};

	const declineForm = useForm(submitDecline, defaultDeclineData);

	// Claim value form
	const defaultClaimValueData = {
		cost_of_parts: '',
		cost_of_labour: '',
	};

	const submitClaimValue = async (data: typeof defaultClaimValueData) => {
		return axios.patch(
			route(API_ENDPOINTS.ADMIN.CLAIM.CLAIM_VALUE, {
				claimId: String(claimId),
			}),
			data
		);
	};

	const claimValueForm = useForm(submitClaimValue, defaultClaimValueData);

	const claimValueTotal = useMemo(() => {
		const parts = parseFloat(claimValueForm.store.cost_of_parts) || 0;
		const labour = parseFloat(claimValueForm.store.cost_of_labour) || 0;
		const total = parts + labour;
		return total.toLocaleString('en-GB', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	}, [claimValueForm.store.cost_of_parts, claimValueForm.store.cost_of_labour]);

	useEffect(() => {
		fetchClaimData();
	}, []);

	useEffect(() => {
		if (declineForm.isSuccess) {
			declineForm.setIsSuccess(false);
			fetchClaimData();
			triggerEventLogRefetch();
			closeActionModal();
		}
	}, [declineForm.isSuccess]);

	useEffect(() => {
		if (claimValueForm.isSuccess) {
			claimValueForm.setIsSuccess(false);
			fetchClaimData();
			triggerEventLogRefetch();
			closeActionModal();
		}
	}, [claimValueForm.isSuccess]);

	const fetchClaimData = useCallback(async () => {
		try {
			const { data } = await axios.get<{ data: AdminClaimViewData }>(
				route(API_ENDPOINTS.ADMIN.CLAIM.VIEW, {
					claimId: String(claimId),
				})
			);
			setValues(data.data);
		} catch (err: any) {
			customToast({
				title: err?.response?.data?.message || 'An error occurred.',
				variant: Variants.Danger,
			});
		}
	}, [claimId]);

	const handleStatusUpdate = async () => {
		if (!activeStatusModal) return;

		setDisabled(true);

		try {
			const { data } = await axios.patch(
				route(API_ENDPOINTS.ADMIN.CLAIM.STATUS_UPDATE, {
					claimId: String(claimId),
				}),
				{ status_action: activeStatusModal }
			);

			customToast({
				title: data?.message || 'Success.',
				variant: Variants.Success,
			});

			fetchClaimData();
			triggerEventLogRefetch();
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			closeStatusModal();
		}
	};

	const handleApprove = async () => {
		setDisabled(true);

		try {
			const { data } = await axios.patch(
				route(API_ENDPOINTS.ADMIN.CLAIM.APPROVE, {
					claimId: String(claimId),
				})
			);

			customToast({
				title: data?.message || 'Success.',
				variant: Variants.Success,
			});

			fetchClaimData();
			triggerEventLogRefetch();
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			closeActionModal();
		}
	};

	const getActions = (): DropdownItemProps[] => {
		if (!store || !store.permissions) return [];

		let actions: DropdownItemProps[] = [];

	if (store?.permissions?.export) {
		actions.push({
			label: StringHelpers.title(
				crud?.buttons?.export?.default || 'Export'
			),
			onClick: async () => {
				setDisabled(true);
				try {
					await exportClaims({
						claimIds: [String(claimId)],
						fallbackFilename: `${new Date().toISOString().split('T')[0]}-company-claim-export.csv`,
						successMessage: replaceKeyWithValue(
							responses?.export,
							'attribute',
							StringHelpers.lower(
								crud?.models?.claim
								|| 'claim'
							)
						),
					});
				} catch (err: any) {
					customToast({
						title:
							err?.response?.data?.message ||
							'An error occurred.',
						variant: Variants.Danger,
					});
				} finally {
					setDisabled(false);
				}
			},
			variant: Variants.Dark,
			icon: Icons.DOWNLOAD,
			disabled: disabled,
		});
	}

		if (store?.permissions?.reviewed) {
			actions.push({
				label: StringHelpers.title(
					fields?.reviewed || 'Reviewed'
				),
				onClick: () => openStatusModal('reviewed'),
				variant: Variants.Dark,
				icon: Icons.CHECKBOX_ON,
				disabled: disabled,
			});
		}

		if (store?.permissions?.ordered) {
			actions.push({
				label: StringHelpers.title(
					fields?.ordered || 'Ordered'
				),
				onClick: () => openStatusModal('ordered'),
				variant: Variants.Dark,
				icon: Icons.START,
				disabled: disabled,
			});
		}

		if (store?.permissions?.shipped) {
			actions.push({
				label: StringHelpers.title(
					fields?.shipped || 'Shipped'
				),
				onClick: () => openStatusModal('shipped'),
				variant: Variants.Dark,
				icon: Icons.MAIL_SEND,
				disabled: disabled,
			});
		}

		if (store?.permissions?.approve) {
			actions.push({
				label: StringHelpers.title(
					crud?.buttons?.approve?.default || 'Approve'
				),
				onClick: () => openActionModal('approve'),
				variant: Variants.Success,
				icon: Icons.CHECK_CIRCLE,
				disabled: disabled,
			});
		}

		if (store?.permissions?.reject) {
			actions.push({
				label: StringHelpers.title(
					crud?.buttons?.decline?.default || 'Decline'
				),
				onClick: () => openActionModal('decline'),
				variant: Variants.Danger,
				icon: Icons.WARNING,
				disabled: disabled,
			});
		}

		if (store?.permissions?.claim_value) {
			actions.push({
				label: StringHelpers.title(
					fields?.claim_value || 'Claim Value'
				),
				onClick: () => openActionModal('claim_value'),
				variant: Variants.Dark,
				icon: Icons.EDIT,
				disabled: disabled,
			});
		}

		return actions;
	};

	const title = useMemo(
		() =>
			StringHelpers.title(
				`${replaceKeyWithValue(
					crud?.pages?.view?.title || 'View :model',
					'model',
					crud?.models?.claim || 'Claim'
				)} ${crud?.sub_titles?.details || 'Details'}`
			),
		[crud]
	);

	const address = store?.customer?.primary_site?.address;
	const contactIndividual = store?.customer?.primary_individual;

	return (
		<PageLayout title={title}>
			<WidthConstrainedContainer>
				<div className="mb-4 d-flex justify-content-between align-items-center">
					<Button
						label={StringHelpers.title(
							crud?.buttons?.back?.default || 'Back'
						)}
						onClick={() => {
							const savedFilter = sessionStorage.getItem(SEARCH_FILTERS.CLAIM);
							sessionStorage.removeItem(SEARCH_FILTERS.CLAIM);
							navigate(
								savedFilter
									? `/claims?filter[search]=${encodeURIComponent(savedFilter)}`
									: '/claims'
							);
						}}
						variant={Variants.Dark}
						type={ButtonTypes.Outline}
						disabled={disabled}
					/>
					{getActions().length > 0 && (
						<Dropdown
							label={StringHelpers.title(
								crud?.buttons?.actions?.default ||
									'Actions'
							)}
							items={getActions()}
							type={ButtonTypes.Solid}
							variant={Variants.Dark}
							disabled={disabled}
						/>
					)}
				</div>

				{/* CONTACT DETAILS */}
				<Card>
					<CardBody>
						<SubTitle
							className="mb-3 w-100"
							style={{ color: '#464C5E' }}
							title={`${
								crud?.models?.contact ||
								'Contact'
							} ${
								crud?.sub_titles?.details ||
								'Details'
							}`}
						/>

						<div className="row">
							<div className="col-12 col-lg-6 col-xxl-3">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											crud?.models?.contact ||
												'Contact'
										)}
									</strong>
									<div>
										{store?.customer_full_name ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col-12 col-lg-6 col-xxl-3">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.email ||
												'Email'
										)}
									</strong>
									<div>
										{contactIndividual?.email ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col-12 col-lg-6 col-xxl-3">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.mobile_phone_number ||
												'Mobile'
										)}
									</strong>
									<div>
										{contactIndividual?.display_mobile_number ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col-12 col-lg-6 col-xxl-3">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.landline_phone_number ||
												'Landline'
										)}
									</strong>
									<div>
										{contactIndividual?.display_landline_number ||
											'-'}
									</div>
								</div>
							</div>
						</div>
					</CardBody>
				</Card>

				{/* CUSTOMER COMPANY DETAILS */}
				<Card>
					<CardBody>
						<SubTitle
							className="mb-3 w-100"
							style={{ color: '#464C5E' }}
							title={`${
								crud?.models?.company ||
								'Company'
							} ${
								crud?.sub_titles?.details ||
								'Details'
							}`}
						/>

						<div className="row">
							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.company_name ||
												'Company name'
										)}
									</strong>
									<div>
										{store?.customer?.name ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.address_line_1 ||
												'Address line 1'
										)}
									</strong>
									<div>
										{address?.address_line_1 ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.address_line_2 ||
												'Address line 2'
										)}
									</strong>
									<div>
										{address?.address_line_2 ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.address_line_3 ||
												'Address line 3'
										)}
									</strong>
									<div>
										{address?.address_line_3 ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.town_city ||
												'Town / City'
										)}
									</strong>
									<div>
										{address?.town_city || '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.state_county ||
												'State / County'
										)}
									</strong>
									<div>
										{address?.state_county ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.postcode_zipcode ||
												'Postcode / Zipcode'
										)}
									</strong>
									<div>
										{address?.postcode_zipcode ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.country_code_iso_3 ||
												'Country'
										)}
									</strong>
									<div>
										{address?.country || '-'}
									</div>
								</div>
							</div>
						</div>
					</CardBody>
				</Card>

				{/* CLAIM DETAILS */}
				<Card>
					<CardBody>
						<SubTitle
							className="mb-3 w-100"
							style={{ color: '#464C5E' }}
							title={`${
								crud?.models?.claim || 'Claim'
							} ${
								crud?.sub_titles?.details ||
								'Details'
							}`}
						/>

						<div className="row">
							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											crud?.models?.company ||
												'Company'
										)}
									</strong>
									<div>
										{store?.company?.name ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.created_by ||
												'Created by'
										)}
									</strong>
									<div>
										{store?.created_by_individual?.name ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											crud?.models?.installation ||
												'Installation'
										)}
									</strong>
									<div>
										{store?.toshiba_installation
											? [
												store.toshiba_installation.installer_company_name,
												store.toshiba_installation.product_child_name,
											].filter(Boolean).join(' - ') || '-'
											: '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.serial_number ||
												'Serial number'
										)}
									</strong>
									<div>
										{store?.serial_number ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.product_model ||
												'Product model'
										)}
									</strong>
									<div>
										{store?.product_model ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.installation_date ||
												'Installation date'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.installation_date,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											crud?.sub_titles
												?.distributor ||
												'Distributor'
										)}
									</strong>
									<div>
										{store?.distributor?.name ||
											'-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.status ||
												'Status'
										)}
									</strong>
									<div>
										{store?.status_display ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.approved_by ||
												'Approved by'
										)}
									</strong>
									<div>
										{store
											?.approved_by_individual
											?.name || '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.approval_date ||
												'Approval date'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.approval_date,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.declined_by ||
												'Declined by'
										)}
									</strong>
									<div>
										{store
											?.declined_by_individual
											?.name || '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.declined_date ||
												'Declined date'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.declined_date,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.reason ||
												'Reason'
										)}
									</strong>
									<div>
										{store?.declined_reason ||
											'-'}
									</div>
								</div>
							</div>

							<div className="col">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.created_at ||
												'Date of claim'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.created_at,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.date_of_failure ||
												'Date of failure'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.date_of_failure,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>

								{store?.distributor?.distributor_claim_type === DISTRIBUTOR_CLAIM_TYPES.POST_REPAIR 
									&& (<div className="d-flex flex-column mb-3">
											<strong>
												{StringHelpers.title(
													fields?.date_of_repair ||
														'Date of repair'
												)}
											</strong>
											<div>
												{formatDateFns(
													store?.date_of_repair,
													DateFormats.DATE
												) || '-'}
											</div>
										</div>
									)
								}

								{store?.distributor?.distributor_claim_type === DISTRIBUTOR_CLAIM_TYPES.PRE_REPAIR 
									&& (<div className="d-flex flex-column mb-3">
											<strong>
												{StringHelpers.title(
													fields?.proposed_date_of_repair ||
														'proposed date of repair'
												)}
											</strong>
											<div>
												{formatDateFns(
													store?.proposed_date_of_repair,
													DateFormats.DATE
												) || '-'}
											</div>
										</div>
									)
								}

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.quantity_of_parts ||
												'Number of parts'
										)}
									</strong>
									<div>
										{store?.parts_count ?? '-'}
									</div>
								</div>

								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.updated_at ||
												'Last updated'
										)}
									</strong>
									<div>
										{formatDateFns(
											store?.updated_at,
											DateFormats.DATE
										) || '-'}
									</div>
								</div>
							</div>
						</div>
					</CardBody>
				</Card>

				{/* PARTS DETAILS */}
				{store?.parts && store.parts.length > 0 && (
					<Card>
						<CardBody>
							<SubTitle
								className="mb-3 w-100"
								style={{ color: '#464C5E' }}
								title={`${
									crud?.models?.part ||
									'Part'
								} ${
									crud?.sub_titles?.details ||
									'Details'
								}`}
							/>

							{store.parts.map(
								(
									part: ClaimPart,
									index: number
								) => (
									<div
										key={part.id || index}
										className={
											index < store.parts.length - 1
												? 'mb-4 pb-4 border-bottom'
												: ''
										}
									>
										<div className="row">
											<div className="col">
												<div className="d-flex flex-column mb-3">
													<strong>
														{StringHelpers.title(
															fields?.part_number ||
																'Part number'
														)}
													</strong>
													<div>
														{part.part_number ||
															'-'}
													</div>
												</div>
											</div>

											<div className="col">
												<div className="d-flex flex-column mb-3">
													<strong>
														{StringHelpers.title(
															fields?.description ||
																'Description'
														)}
													</strong>
													<div>
														{part.description ||
															'-'}
													</div>
												</div>
											</div>

											{part.replacement_dispatch_note_number && (
												<div className="col">
													<div className="d-flex flex-column mb-3">
														<strong>
															{StringHelpers.title(
																fields?.replacement_dispatch_note_number ||
																	'Replacement despatch note number'
															)}
														</strong>
														<div>
															{part.replacement_dispatch_note_number}
														</div>
													</div>
												</div>
											)}

											<div className="col">
												<div className="d-flex flex-column mb-3">
													<strong>
														{StringHelpers.title(
															fields?.previously_repaired ||
																'Previously replaced'
														)}
													</strong>
													<div>
														{part.previously_repaired !== null
															? part.previously_repaired
																? 'Yes'
																: 'No'
															: '-'}
													</div>
												</div>
											</div>
										</div>
									</div>
								)
							)}
						</CardBody>
					</Card>
				)}

				{/* CLAIM VALUE DETAILS */}
				{(store?.cost_of_parts || store?.cost_of_labour || store?.total_value) && (
					<Card>
						<CardBody>
							<SubTitle
								className="mb-3 w-100"
								style={{ color: '#464C5E' }}
								title={`${
									crud?.models?.claim || 'Claim'
								} ${
									fields?.value || 'Value'
								} ${
									crud?.sub_titles?.details ||
									'Details'
								}`}
							/>

							<div className="row">
								{store?.cost_of_parts && (
									<div className="col">
										<div className="d-flex flex-column mb-3">
											<strong>
												{StringHelpers.title(
													fields?.cost_of_parts ||
														'Cost of parts'
												)}
											</strong>
											<div>
												{`\u00A3${store.cost_of_parts}`}
											</div>
										</div>
									</div>
								)}

								{store?.cost_of_labour && (
									<div className="col">
										<div className="d-flex flex-column mb-3">
											<strong>
												{StringHelpers.title(
													fields?.cost_of_labour ||
														'Cost of labour'
												)}
											</strong>
											<div>
												{`\u00A3${store.cost_of_labour}`}
											</div>
										</div>
									</div>
								)}

								{store?.total_value && (
									<div className="col">
										<div className="d-flex flex-column mb-3">
											<strong>
												{StringHelpers.title(
													fields?.total_value ||
														'Total value'
												)}
											</strong>
											<div>
												{`\u00A3${store.total_value}`}
											</div>
										</div>
									</div>
								)}
							</div>
						</CardBody>
					</Card>
				)}

				{hasPermissionAccess(Permissions.TOSHIBA_CLAIM_AUDIT_LOG_INDEX) 
					&& <ClaimEventLog refetchKey={eventLogRefetchKey}/>
				}
			</WidthConstrainedContainer>

			{activeStatusModal && (
				<Modal
					open={activeStatusModal !== null}
					onClose={closeStatusModal}
					title={StringHelpers.title(
						crud?.modals?.status_update?.[activeStatusModal]?.title ||
						`Mark as ${activeStatusModal}`
					)}
					confirmText={
						disabled
							? crud?.buttons?.edit?.submitting || 'Updating...'
							: crud?.buttons?.edit?.default || 'Update'
					}
					closeText={crud?.buttons?.cancel?.default || 'Cancel'}
					onConfirm={handleStatusUpdate}
					disabled={disabled}
					variant={Variants.Primary}
				>
					{crud?.modals?.status_update?.[activeStatusModal]?.description?.map(
						(item: string, key: number) => (
							<p key={key}>{item}</p>
						)
					) || (
						<p>Are you sure you want to update this claim status?</p>
					)}
				</Modal>
			)}

			{activeActionModal === 'approve' && (
				<Modal
					open={true}
					onClose={closeActionModal}
					title={StringHelpers.title(
						replaceKeyWithValue(
							crud?.modals?.approve?.title || 'Approve :name',
							'name',
							crud?.models?.claim || 'Claim'
						)
					)}
					confirmText={
						disabled
							? crud?.buttons?.approve?.submitting || 'Approving...'
							: crud?.buttons?.approve?.default || 'Approve'
					}
					closeText={crud?.buttons?.cancel?.default || 'Cancel'}
					onConfirm={handleApprove}
					disabled={disabled}
					variant={Variants.Success}
				>
					{
						<p>
							{replaceKeyWithValue(
								crud?.modals?.approve?.alt_description
									|| 'Are you sure you want to approve this :name?',
								'name',
								StringHelpers.lower(
									crud?.models?.claim 
									|| 'claim'
								)
							)}
						</p>	
					}
				</Modal>
			)}

			{activeActionModal === 'decline' && (
				<Modal
					open={true}
					onClose={closeActionModal}
					title={StringHelpers.title(
						replaceKeyWithValue(
							crud?.modals?.decline?.title || 'Decline :name',
							'name',
							crud?.models?.claim || 'Claim'
						)
					)}
					closeText={crud?.buttons?.cancel?.default || 'Cancel'}
					disabled={disabled}
					variant={Variants.Danger}
					overriddenConfirmButton={
						<declineForm.Submit
							defaultLabel={crud?.buttons?.decline?.default || 'Decline'}
							loadingLabel={crud?.buttons?.decline?.submitting || 'Declining...'}
							variant={Variants.Danger}
						/>
					}
				>
					{
						<p>
							{replaceKeyWithValue(
								crud?.modals?.decline?.description
									|| 'Are you sure you want to decline this :name?',
								'name',
								StringHelpers.lower(
									crud?.models?.claim 
									|| 'claim'
								)
							)}
						</p>
					}
					{declineForm.Input({
						name: 'declined_reason',
						label: StringHelpers.title(
							fields?.reason || 'Reason'
						),
						type: 'textarea',
					})}
				</Modal>
			)}

			{activeActionModal === 'claim_value' && (
				<Modal
					open={true}
					onClose={closeActionModal}
					title={StringHelpers.title(
						fields?.claim_value || 'Claim Value'
					)}
					closeText={crud?.buttons?.cancel?.default || 'Cancel'}
					disabled={disabled}
					variant={Variants.Primary}
					overriddenConfirmButton={
						<claimValueForm.Submit
							defaultLabel={crud?.buttons?.edit?.default || 'Update'}
							loadingLabel={crud?.buttons?.edit?.submitting || 'Updating...'}
							variant={Variants.Primary}
						/>
					}
				>
					{claimValueForm.Input({
						name: 'cost_of_parts',
						label: StringHelpers.title(
							fields?.cost_of_parts || 'Cost of parts'
						) + ' (\u00A3)',
						type: 'text',
					})}
					{claimValueForm.Input({
						name: 'cost_of_labour',
						label: StringHelpers.title(
							fields?.cost_of_labour || 'Cost of labour'
						) + ' (\u00A3)',
						type: 'text',
					})}
					<div className="form-floating mb-4">
						<input
							id="total_value"
							name="total_value"
							type="text"
							className="form-control"
							value={`\u00A3${claimValueTotal}`}
							disabled
							readOnly
						/>
						<label htmlFor="total_value">
							{StringHelpers.title(
								fields?.total_value || 'Total value'
							) + ' (\u00A3)'}
						</label>
					</div>
				</Modal>
			)}
		</PageLayout>
	);
}

export default ClaimView;
